// No future-daily cascade: the master endpoint's own `futureStops` covers it.
import { isOnRequestStop } from 'types/modules/routing'
import { dayOfWeekForServiceDate } from 'utils/routeStopOperations/v2/dayOfWeek'
import { makeAssignDailySlotStep } from 'utils/routeStopOperations/v2/steps/assignDailySlot'
import { makeAssignMasterSlotStep } from 'utils/routeStopOperations/v2/steps/assignMasterSlot'
import { makePreflightStep } from 'utils/routeStopOperations/v2/steps/preflight'
import { makeSequenceDailySlotStep } from 'utils/routeStopOperations/v2/steps/sequenceDailySlot'
import { makeSequenceMasterSlotStep } from 'utils/routeStopOperations/v2/steps/sequenceMasterSlot'
import { makeVerifyStep } from 'utils/routeStopOperations/v2/steps/verify'

import type { AssignIntent, Deps, OperationPlan, PipelineStep } from 'types/modules/routing'

const STRATEGY_ID = 'v3-slot' as const

/**
 * The CSE gate on `pipelines/assign.ts` keeps one-off jobs off the weekly template; slot keying does
 * not inherit it. A routable row stays — no work order, and master is where a recurring one belongs.
 */
const masterEligibleRows = (stops: AssignIntent['stops']): AssignIntent['stops'] =>
  stops.filter(stop => !(isOnRequestStop(stop) && stop.workOrderId))

export const buildAssignSlotPipeline = (intent: AssignIntent, deps: Deps): OperationPlan => {
  const steps: PipelineStep[] = [makePreflightStep({ intent }, deps) as PipelineStep]

  steps.push(
    makeAssignDailySlotStep(
      { routeId: intent.routeId, serviceDate: intent.serviceDate, rows: intent.stops, startSequence: intent.startSequence },
      deps
    ) as PipelineStep
  )

  if (intent.allOrderedStops && intent.allOrderedStops.length > 0) {
    steps.push(
      makeSequenceDailySlotStep(
        { routeId: intent.routeId, serviceDate: intent.serviceDate, orderedRows: intent.allOrderedStops },
        deps
      ) as PipelineStep
    )
  }

  const dayOfWeek = intent.includeMaster === true ? dayOfWeekForServiceDate(intent.serviceDate) : null
  const masterRows = dayOfWeek ? masterEligibleRows(intent.stops) : []
  if (dayOfWeek && masterRows.length > 0) {
    steps.push(
      makeAssignMasterSlotStep(
        {
          routeId: intent.routeId,
          dayOfWeek,
          rows: masterRows,
          futureStops: true,
          futureStart: intent.serviceDate,
          startSequence: intent.startSequence,
        },
        deps
      ) as PipelineStep
    )

    // The master assign lands every slot at the head, so master needs the daily leg's correction too.
    // Resequencing reads the full order: `sequenceMasterSlot` narrows it to the slots master holds.
    if (intent.allOrderedStops && intent.allOrderedStops.length > 0) {
      steps.push(
        makeSequenceMasterSlotStep({ routeId: intent.routeId, dayOfWeek, orderedRows: intent.allOrderedStops }, deps) as PipelineStep
      )
    }
  }

  steps.push(
    makeVerifyStep({ intent, expectedAssignedWorkOrderIds: intent.stops.map(stop => String(stop.workOrderId)) }, deps) as PipelineStep
  )

  return { strategy: STRATEGY_ID, intent, steps }
}
